import { useEffect, useState } from "react";

import { Bot, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/cinch/Field";
import { Textarea } from "@/components/ui/textarea";
import { useCan } from "@/lib/auth/useCan";
import { useUser } from "@/lib/auth/useUser";

import { useDeleteNote, useMoveNote, useUpdateNote, type Note, type Workspace } from "../api";
import { BOARD_META, SWOT_BOARDS } from "../constants";

/** Detail/edit dialog for a SWOT note: content edits (author or admin), a
 *  cross-board move (anyone — “this weakness is really a threat”), delete, and
 *  the AI review trail. */
export function SwotNoteDialog({
  note,
  workspace,
  onOpenChange,
}: {
  note: Note | null;
  workspace: Workspace;
  onOpenChange: (open: boolean) => void;
}) {
  const user = useUser();
  const isAdmin = useCan("planning.admin");
  const update = useUpdateNote(workspace.session.id);
  const move = useMoveNote(workspace.session.id);
  const del = useDeleteNote(workspace.session.id);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (note) {
      setTitle(note.title);
      setDescription(note.description ?? "");
    }
  }, [note?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!note) return null;
  const canEdit = isAdmin || note.author_id === user.id;
  const dirty = title !== note.title || description !== (note.description ?? "");

  const save = () => {
    update.mutate(
      { id: note.id, title: title.trim(), description: description.trim() || null },
      {
        onSuccess: () => toast.success("Note updated"),
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <Dialog open={note !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{canEdit ? "Edit note" : note.title}</DialogTitle>
          <DialogDescription>
            {note.author_name ? `By ${note.author_name} · ` : ""}
            {BOARD_META[note.board].label}
            {note.status === "draft" ? " · draft" : ""}
          </DialogDescription>
        </DialogHeader>

        {canEdit ? (
          <div className="space-y-3">
            <Field
              id="note-title"
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <Textarea
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
            />
          </div>
        ) : note.description ? (
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{note.description}</p>
        ) : null}

        {note.ai_review?.question || note.ai_clarification ? (
          <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-xs">
            <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
            <div className="space-y-1">
              {note.ai_review?.question ? (
                <p className="text-muted-foreground">AI asked: {note.ai_review.question}</p>
              ) : null}
              {note.ai_clarification ? (
                <p>
                  <span className="font-medium">{note.author_name ?? "Author"}:</span>{" "}
                  {note.ai_clarification}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        {note.status === "posted" ? (
          <div className="space-y-1.5">
            <div className="text-xs font-medium text-muted-foreground">Move to another board</div>
            <Select
              value={note.board}
              onValueChange={(board) =>
                move.mutate(
                  { id: note.id, board: board as (typeof SWOT_BOARDS)[number] },
                  {
                    onSuccess: () =>
                      toast.success(`Moved to ${BOARD_META[board as keyof typeof BOARD_META].label}`),
                    onError: (err) => toast.error(err.message),
                  },
                )
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SWOT_BOARDS.map((b) => (
                  <SelectItem key={b} value={b}>
                    {BOARD_META[b].label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null}

        <DialogFooter className="items-center gap-2 sm:justify-between">
          {canEdit ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() =>
                del.mutate(note.id, {
                  onSuccess: () => {
                    toast.success("Note deleted");
                    onOpenChange(false);
                  },
                  onError: (err) => toast.error(err.message),
                })
              }
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" /> Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
            {canEdit ? (
              <Button onClick={save} disabled={!dirty || !title.trim() || update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
